import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { Users, Music, Coffee, Camera, BookOpen, Baby, Globe, HandHeart, CheckCircle, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ministriesData } from '../data/ministriesData';
export function JoinTeamPage() {
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const toggleTeam = (teamId: string) => {
    setSelectedTeams(prev => prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTeams.length === 0) {
      alert('Please select at least one team you are interested in.');
      return;
    }
    // Simulate API call
    console.log('Form submitted:', {
      ...formData,
      teams: selectedTeams
    });
    setSubmitted(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  if (submitted) {
    return <Layout data-id="element-937">
        <div className="min-h-[70vh] flex items-center justify-center bg-[#F5F7FA]" data-id="element-938">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white p-10 md:p-16 rounded-2xl shadow-xl max-w-2xl w-full text-center mx-4" data-id="element-939">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-940">
              <CheckCircle className="w-10 h-10 text-green-600" data-id="element-941" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-942">
              Thank You!
            </h1>
            <p className="text-gray-600 text-lg mb-8" data-id="element-943">
              We're excited that you want to serve! A ministry leader will reach
              out to you soon to discuss next steps.
            </p>
            <Link to="/ministries" data-id="element-944">
              <Button rightIcon={<ArrowRight className="w-4 h-4" data-id="element-946" />} data-id="element-945">
                Explore Our Ministries
              </Button>
            </Link>
          </motion.div>
        </div>
      </Layout>;
  }
  return <Layout data-id="element-947">
      {/* Hero Section */}
      <section className="relative bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-white overflow-hidden" data-id="element-948">
        <div className="absolute inset-0 opacity-20" data-id="element-949">
          <img src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Team serving together" className="w-full h-full object-cover" data-id="element-950" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/80 to-[#0A1F44]" data-id="element-951" />

        <div className="container-custom relative z-10" data-id="element-952">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl" data-id="element-953">
            <span className="inline-block text-[#fc0719] text-sm font-semibold tracking-widest uppercase mb-4" data-id="element-954">
              Serve with Us
            </span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6" data-id="element-955">
              Join a Team
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-white/80 leading-relaxed" data-id="element-956">
              God has given each of us unique gifts to build up the church.
              Discover your purpose by serving others in one of our ministries.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Why Serve Section */}
      <section className="py-16 bg-white border-b" data-id="element-957">
        <div className="container-custom" data-id="element-958">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-959">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center" data-id="element-960">
              <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-961">
                <Users className="w-8 h-8 text-[#0A1F44]" data-id="element-962" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-963">
                Find Community
              </h3>
              <p className="text-gray-600" data-id="element-964">
                Serving alongside others is one of the best ways to build
                lasting relationships.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-center" data-id="element-965">
              <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-966">
                <BookOpen className="w-8 h-8 text-[#0A1F44]" data-id="element-967" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-968">
                Grow Spiritually
              </h3>
              <p className="text-gray-600" data-id="element-969">
                As you pour out into others, you'll find yourself growing deeper
                in your own faith.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="text-center" data-id="element-970">
              <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-971">
                <Globe className="w-8 h-8 text-[#0A1F44]" data-id="element-972" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-973">
                Make an Impact
              </h3>
              <p className="text-gray-600" data-id="element-974">
                Your unique gifts and talents can make an eternal difference in
                someone's life.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-975">
        <div className="container-custom" data-id="element-976">
          <div className="max-w-4xl mx-auto" data-id="element-977">
            <div className="text-center mb-12" data-id="element-978">
              <h2 className="text-3xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-979">
                Where would you like to serve?
              </h2>
              <p className="text-gray-600" data-id="element-980">
                Select one or more teams you're interested in joining, then fill
                out your details below.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-2xl shadow-lg" data-id="element-981">
              {/* Team Selection Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-982">
                {ministriesData.map((team, index) => {
                const isSelected = selectedTeams.includes(team.id);
                return <motion.button key={team.id} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: index * 0.05
                }} onClick={() => toggleTeam(team.id)} type="button" className={`relative p-6 rounded-xl text-left transition-all ${isSelected ? 'bg-[#0A1F44] text-white shadow-lg scale-[1.02]' : 'bg-white hover:shadow-md border border-gray-100'}`} data-id="element-983">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${isSelected ? 'bg-[#fc0719]' : 'bg-[#F5F7FA]'}`} data-id="element-984">
                        <team.icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-[#0A1F44]'}`} data-id="element-985" />
                      </div>
                      <h3 className={`font-bold mb-2 ${isSelected ? 'text-white' : 'text-[#0A1F44]'}`} data-id="element-986">
                        {team.title}
                      </h3>
                      <p className={`text-sm mb-3 ${isSelected ? 'text-white/80' : 'text-gray-600'}`} data-id="element-987">
                        {team.shortDescription}
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-988">
                        {team.features.slice(0, 2).map(role => <span key={role} className={`text-xs px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-989">
                            {role}
                          </span>)}
                        {team.features.length > 2 && <span className={`text-xs px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-990">
                            +{team.features.length - 2} more
                          </span>}
                      </div>
                    </motion.button>;
              })}
              </div>

              <hr className="border-gray-200 mb-12" data-id="element-991" />

              {/* Personal Details */}
              <div className="space-y-6 mb-8" data-id="element-992">
                <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-993">
                  Your Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-994">
                  <div data-id="element-995">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-996">
                      First Name *
                    </label>
                    <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors" data-id="element-997" />
                  </div>
                  <div data-id="element-998">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-999">
                      Last Name *
                    </label>
                    <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors" data-id="element-1000" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1001">
                  <div data-id="element-1002">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1003">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors" data-id="element-1004" />
                  </div>
                  <div data-id="element-1005">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1006">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors" data-id="element-1007" />
                  </div>
                </div>

                {selectedTeams.length > 0 && <div data-id="element-1008">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1009">
                      Selected Teams
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-1010">
                      {selectedTeams.map(teamId => {
                    const team = ministriesData.find(t => t.id === teamId);
                    return team ? <span key={teamId} className="inline-flex items-center gap-1 bg-[#0A1F44] text-white px-3 py-1 rounded-full text-sm" data-id="element-1011">
                            {team.title}
                            <button type="button" onClick={() => toggleTeam(teamId)} className="ml-1 hover:text-[#fc0719]" data-id="element-1012">
                              ×
                            </button>
                          </span> : null;
                  })}
                    </div>
                  </div>}

                <div data-id="element-1013">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1014">
                    Tell us a bit about yourself (Optional)
                  </label>
                  <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleInputChange} placeholder="Any previous experience or specific areas you're passionate about?" className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors resize-none" data-id="element-1015" />
                </div>
              </div>

              <div className="flex flex-col items-center" data-id="element-1016">
                <Button type="submit" size="lg" className="w-full md:w-auto" disabled={selectedTeams.length === 0} leftIcon={<Send className="w-4 h-4" data-id="element-1018" />} data-id="element-1017">
                  Submit Application
                </Button>
                {selectedTeams.length === 0 && <p className="text-sm text-gray-500 mt-2" data-id="element-1019">
                    Please select at least one team above to continue.
                  </p>}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}

      {/* Questions CTA */}
      <section className="py-16 bg-[#fc0719]" data-id="element-1020">
        <div className="container-custom text-center" data-id="element-1021">
          <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-1022">
            Have Questions?
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto" data-id="element-1023">
            Not sure where you'd fit best? We'd love to help you discover your
            gifts and find the perfect place to serve.
          </p>
          <Link to="/contact" data-id="element-1024">
            <Button variant="white" size="lg" className="text-[#fc0719]" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-1026" />} data-id="element-1025">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}